import type { SetRequiredDeep } from 'type-fest'
import * as zod from 'zod'
import type { DataStatus, DepartmentPageLine } from '../Device'
import type { SortItem } from '../VuetifyFix'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR, TABLE, WidgetDataPresentationSchema, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type PartialWidget, type PartialWidgetElement, type WidgetConstructor, type WidgetElementConstructor, type WidgetElementData } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'

export type TableHeaderData = TableWidgetHeaderData & {
  title: string
  headerSlot: `header.${string}`
  itemSlot: `item.${string}`
}

export type RowsTableWidgetSettingsData = RowsTableWidgetSettings & {
  currentIndex: number
  text: string
  value: string
}

export type ColumnsTableWidgetSettingsData = ColumnsTableWidgetSettings & {
  text: string
  value: string
}

export type TableWidgetOptions = {
  headers: TableHeaderData[]
  rows: Record<string, TableWidgetElementData>[]
  rowsSettings: RowsTableWidgetSettingsData[]
  columnsSettings: ColumnsTableWidgetSettingsData[]
  sortItems: SortItem[]
}
export type TableWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'TABLE'
  widget: TableWidget
}
export type TableWidgetOptionsResponse = WidgetDataResponse & {
  type: 'TABLE'
  options: TableWidgetOptions
}
const WidgetElementDataFilterSchema = zod.union([
  zod.literal('SHOW_ALL'),
  zod.literal('SHOW_ONLY_VALUES'),
  zod.literal('SHOW_VALUES_AND_UNITS'),
])
export type WidgetElementDataFilter = zod.infer<typeof WidgetElementDataFilterSchema>

export const TableWidgetElementSchema = WidgetElementSchema.extend({
  type: TABLE,
  lineNumber: zod.number().nullable(),
  yAxisNumber: zod.number(),
  label: zod.string().min(1),
  dataFilter: WidgetElementDataFilterSchema,
})
export type TableWidgetElement = zod.infer<typeof TableWidgetElementSchema>
export type TableWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<TableWidgetElement>, 'type'>

const TableWidgetSettingsSchema = zod.object({
  visible: zod.boolean(),
  identifiers: zod.string().array(),
})

const RowsTableWidgetSettingsSchema = TableWidgetSettingsSchema.extend({})
export type RowsTableWidgetSettings = zod.infer<typeof RowsTableWidgetSettingsSchema>

const ColumnsTableWidgetSettingsSchema = TableWidgetSettingsSchema.extend({
  currentIndex: zod.number(),
  sortOrder: zod.number().nullable(),
  sortDesc: zod.boolean().nullable(),
})
export type ColumnsTableWidgetSettings = zod.infer<typeof ColumnsTableWidgetSettingsSchema>

export const TableWidgetSchema = WidgetSchema.extend({
  type: TABLE,
  dataPresentation: WidgetDataPresentationSchema,
  fixedTitlesAndDescriptions: zod.boolean(),
  rowsSettings: RowsTableWidgetSettingsSchema.array(),
  columnsSettings: ColumnsTableWidgetSettingsSchema.array(),
  elements: TableWidgetElementSchema.array(),
})
export type TableWidget = zod.infer<typeof TableWidgetSchema>
export type TableWidgetPartial = SetRequiredDeep<PartialWidget<TableWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface TableWidgetElementData extends WidgetElementData {
  dataFilter: WidgetElementDataFilter
  yaxisNumber: number
  identifiers: string[]
  label: string
  readOnly: boolean
}
export interface TableWidgetHeaderData {
  text: string
  value: string
  index: number
  number: number
  sortable: boolean
  status: DataStatus
  identifiers: string[]
}
export interface TableWidgetData extends AbstractWidgetData<TableWidgetElementData> {
  type: 'TABLE'
  elements: TableWidgetElementData[]
  errorStatuses: DataStatus[]
  headers: TableWidgetHeaderData[]
  data: Record<string, TableWidgetElementData>[]
}
export interface TableWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'TABLE'
  line: DepartmentPageLine
}
export const DEFAULT_TABLE_WIDGET_ELEMENT_CONSTRUCTOR: WidgetElementConstructor<TableWidgetElementPartial> = () => ({
  ...COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR(),
  dataFilter: 'SHOW_ALL',
  yAxisNumber: 1,
  name: null,
  type: 'TABLE',
})
export const TABLE_WIDGET_CONSTRUCTOR: WidgetConstructor<TableWidgetPartial> = () => ({
  type: 'TABLE',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  dataPresentation: 'COLUMNS',
  fixedTitlesAndDescriptions: false,
  columnsSettings: [],
  rowsSettings: [],
  elements: [DEFAULT_TABLE_WIDGET_ELEMENT_CONSTRUCTOR()],
})
